import React, { useEffect, useState } from 'react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts'
import { getPeriodDayKeys } from '../../utils/statsPeriod'
import { CHART_TYPES } from '../../utils/useChartType'

const formatDayLabel = (dateKey) => {
  const [, month, day] = dateKey.split('-')
  return `${day}/${month}`
}

const getFreshness = (lastEventAt, now) => {
  if (!lastEventAt) return null
  const diffHours = (now - lastEventAt) / (1000 * 60 * 60)
  if (diffHours < 1) {
    return { dot: 'bg-emerald-500', label: 'Menos de 1h atrás' }
  }
  if (diffHours < 3) {
    return { dot: 'bg-blue-500', label: 'Entre 1h e 3h atrás' }
  }
  if (diffHours < 4) {
    return { dot: 'bg-amber-500', label: 'Entre 3h e 4h atrás' }
  }
  return { dot: 'bg-red-500', label: 'Há mais de 4h' }
}

const formatElapsedTime = (diffMs) => {
  const totalMinutes = Math.floor(diffMs / 60000)
  if (totalMinutes < 1) return 'menos de 1 minuto'

  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60

  const parts = []
  if (days > 0) parts.push(`${days} ${days === 1 ? 'dia' : 'dias'}`)
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hora' : 'horas'}`)
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`)

  if (parts.length === 1) return parts[0]
  return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`
}

const buildEmptySeries = (period) => getPeriodDayKeys(period).map((key) => ({
  key,
  label: formatDayLabel(key),
  success: 0,
  error: 0
}))

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null
  const success = payload.find(p => p.dataKey === 'success')?.value ?? 0
  const error = payload.find(p => p.dataKey === 'error')?.value ?? 0

  return (
    <div className="bg-black/90 text-white rounded-lg px-3 py-2.5 text-xs border border-white/20 min-w-[140px]">
      <p className="font-semibold mb-1.5 text-gray-200">{label}</p>
      <div className="flex items-center justify-between gap-4 mb-1">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-0.5 rounded-full bg-emerald-400" />
          Sucesso
        </span>
        <span className="font-bold text-emerald-400">{success}</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-0.5 rounded-full bg-red-400" />
          Falhas
        </span>
        <span className="font-bold text-red-400">{error}</span>
      </div>
    </div>
  )
}

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null
  const entry = payload[0]
  return (
    <div className="bg-black/90 text-white rounded-lg px-3 py-2 text-xs border border-white/20">
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.payload.color }} />
        {entry.name}
        <span className="font-bold ml-1">{entry.value}</span>
      </span>
    </div>
  )
}

const SingleDayPie = ({ success, error, isDarkMode }) => {
  const data = [
    { name: 'Sucesso', value: success, color: '#10b981' },
    { name: 'Falhas', value: error, color: error > 0 ? '#ef4444' : (isDarkMode ? '#4b5563' : '#d1d5db') }
  ]
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius="55%"
          outerRadius="85%"
          paddingAngle={success > 0 && error > 0 ? 3 : 0}
          strokeWidth={0}
          isAnimationActive
          animationBegin={0}
          animationDuration={900}
          animationEasing="ease-out"
        >
          {data.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
        </Pie>
        <Tooltip content={<PieTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  )
}

const SupplierAvatar = ({ logoUrl, name }) => {
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [logoUrl])

  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        alt={name || ''}
        className="w-8 h-8 object-contain rounded flex-shrink-0"
        onError={() => setFailed(true)}
      />
    )
  }

  return (
    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
      <span className="material-icons text-blue-600 dark:text-blue-400" style={{ fontSize: '16px' }}>
        sync_alt
      </span>
    </div>
  )
}

const IntegrationEventCard = ({ stat, period, chartType, integration, onOpenTracesPage, onOpenErrorsPage, onTotalsLoaded }) => {
  const [series, setSeries] = useState(buildEmptySeries(period))
  const [lastEventAt, setLastEventAt] = useState(null)
  const [loading, setLoading] = useState(true)
  const [now, setNow] = useState(new Date())
  const [, forceUpdate] = useState({})

  const handleOpenDetails = () => {
    if (!onOpenTracesPage) return
    onOpenTracesPage(
      integration || {
        id: stat.integration_id,
        name: stat.integration_name,
        supplier: stat.supplier_name ? { name: stat.supplier_name, logo_url: stat.supplier_logo_url } : null,
        // absent on an older backend: treat as active instead of wrongly disabling reprocessing
        active: stat.integration_active !== false,
        system: stat.system_name ? { name: stat.system_name } : null
      },
      period
    )
  }

  useEffect(() => {
    const observer = new MutationObserver(() => forceUpdate({}))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!stat?.integration_id) return

    const days = buildEmptySeries(period)
    const bucketByKey = new Map(days.map(d => [d.key, d]))

    ;(stat.daily || []).forEach((entry) => {
      const bucket = bucketByKey.get(entry.date)
      if (bucket) {
        bucket.success += entry.success || 0
        bucket.error += entry.error || 0
      }
    })

    const latestDate = stat.last_event_at ? new Date(stat.last_event_at) : null

    setSeries(days)
    setLastEventAt(latestDate)
    setLoading(false)

    if (onTotalsLoaded) {
      const success = days.reduce((sum, d) => sum + d.success, 0)
      const error = days.reduce((sum, d) => sum + d.error, 0)
      onTotalsLoaded(stat.integration_id, { success, error, processing: stat.processing || 0 })
    }
  }, [stat, period])

  const isDarkMode = document.documentElement.classList.contains('dark')
  const textColor = isDarkMode ? '#c3c2b7' : '#52514e'
  const gridColor = isDarkMode ? 'rgba(55, 65, 81, 0.25)' : 'rgba(229, 231, 235, 0.6)'

  const totalSuccess = series.reduce((sum, d) => sum + d.success, 0)
  const totalError = series.reduce((sum, d) => sum + d.error, 0)
  const totalTraces = totalSuccess + totalError
  const hasData = totalTraces > 0
  const gradientId = `card-gradient-${stat.integration_id}`
  const errorColor = totalError > 0 ? '#ef4444' : (isDarkMode ? '#4b5563' : '#d1d5db')
  const freshness = getFreshness(lastEventAt, now)
  const lastEventDateStr = lastEventAt?.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const lastEventTimeStr = lastEventAt?.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  const lastEventElapsedStr = lastEventAt ? formatElapsedTime(now - lastEventAt) : null

  return (
    <div className="bg-white/80 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl border border-white/60 dark:border-white/10 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_14px_28px_-18px_rgba(0,0,0,0.22)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3),0_14px_28px_-18px_rgba(0,0,0,0.7)] p-4 flex flex-col gap-3 transition-shadow hover:shadow-[0_1px_2px_rgba(0,0,0,0.06),0_18px_32px_-16px_rgba(0,0,0,0.28)] dark:hover:shadow-[0_1px_2px_rgba(0,0,0,0.35),0_18px_32px_-16px_rgba(0,0,0,0.8)]">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <SupplierAvatar logoUrl={stat.supplier_logo_url} name={stat.supplier_name} />
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 dark:text-white truncate" title={stat.integration_name}>
              {stat.integration_name}
            </h3>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {stat.supplier_name && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-100 dark:border-purple-800/40">
                  {stat.supplier_name}
                </span>
              )}
              {stat.system_name && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400">
                  {stat.system_name}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 flex items-center gap-1.5">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/40">
            {stat.event_count} {stat.event_count === 1 ? 'evento' : 'eventos'}
          </span>
          {onOpenTracesPage && (
            <button
              onClick={handleOpenDetails}
              title="Ver detalhes da integração"
              className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
            >
              <span className="material-icons" style={{ fontSize: '15px' }}>arrow_forward</span>
            </button>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="relative" style={{ height: '110px' }}>
        {loading ? (
          <div className="absolute inset-0 flex items-end gap-2 px-1 pb-1 animate-pulse">
            {[45, 70, 35, 85, 55, 65, 40].map((h, idx) => (
              <div
                key={idx}
                className="flex-1 rounded-t bg-gray-200 dark:bg-gray-700/60"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        ) : !hasData ? (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500">
            Sem eventos no período
          </div>
        ) : chartType === CHART_TYPES.PIE ? (
          <SingleDayPie success={totalSuccess} error={totalError} isDarkMode={isDarkMode} />
        ) : chartType === CHART_TYPES.BAR ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={gridColor} strokeDasharray="0" />
              <XAxis
                dataKey="label"
                tick={{ fill: textColor, fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: gridColor }}
                interval={0}
                minTickGap={0}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: textColor, fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                width={28}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }} />
              <Bar dataKey="success" name="Sucesso" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={18}
                isAnimationActive
                animationDuration={900}
                animationEasing="ease-out"
              />
              <Bar dataKey="error" name="Falhas" fill={errorColor} radius={[3, 3, 0, 0]} maxBarSize={18}
                isAnimationActive
                animationDuration={900}
                animationEasing="ease-out"
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id={`${gradientId}-success`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id={`${gradientId}-error`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={errorColor} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={errorColor} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke={gridColor} strokeDasharray="0" />
              <XAxis
                dataKey="label"
                tick={{ fill: textColor, fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: gridColor }}
                interval={0}
                minTickGap={0}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: textColor, fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                width={28}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: gridColor, strokeWidth: 1 }} />
              <Area
                type="monotone"
                dataKey="success"
                name="Sucesso"
                stroke="#10b981"
                strokeWidth={2}
                fill={`url(#${gradientId}-success)`}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
                isAnimationActive
                animationDuration={900}
                animationEasing="ease-out"
              />
              <Area
                type="monotone"
                dataKey="error"
                name="Falhas"
                stroke={errorColor}
                strokeWidth={2}
                fill={`url(#${gradientId}-error)`}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
                isAnimationActive
                animationDuration={900}
                animationEasing="ease-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-4 text-xs pt-2.5 border-t border-gray-100 dark:border-gray-700">
        {loading ? (
          <>
            <span className="h-3.5 w-16 rounded bg-gray-200 dark:bg-gray-700/60 animate-pulse" />
            <span className="h-3.5 w-16 rounded bg-gray-200 dark:bg-gray-700/60 animate-pulse" />
          </>
        ) : (
          <>
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              Sucesso <span className="font-semibold text-gray-900 dark:text-white">{totalSuccess}</span>
            </span>
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
              Falhas <span className="font-semibold text-gray-900 dark:text-white">{totalError}</span>
            </span>
            {totalError > 0 && onOpenErrorsPage && (
              <span className="flex items-center gap-2 ml-1">
                <span className="w-px h-3 bg-gray-200 dark:bg-gray-700" />
                <button
                  onClick={() => onOpenErrorsPage([stat], period)}
                  title="Ver incidências desta integração"
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/60 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 hover:border-red-300 dark:hover:border-red-700 hover:text-red-700 dark:hover:text-red-300 transition"
                >
                  <span className="material-icons leading-none" style={{ fontSize: '12px' }}>north_east</span>
                  Incidências
                </button>
              </span>
            )}
            {stat.processing > 0 && (
              <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                <span className="relative flex w-2 h-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex w-2 h-2 rounded-full bg-blue-500" />
                </span>
                Processando <span className="font-semibold text-gray-900 dark:text-white">{stat.processing}</span>
              </span>
            )}
          </>
        )}
      </div>

      {/* Último evento */}
      {!loading && lastEventAt && (
        <div className="flex items-center justify-between gap-2 pt-2.5 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-start gap-1.5 min-w-0">
            <span
              className={`inline-block w-2 h-2 rounded-full flex-shrink-0 mt-1 ${freshness.dot}`}
              title={freshness.label}
            />
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
                Evento mais recente
              </p>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
                há {lastEventElapsedStr}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 text-xs text-gray-600 dark:text-gray-300">
            <span className="flex items-center gap-1">
              <span className="material-icons text-gray-400 dark:text-gray-500" style={{ fontSize: '14px' }}>
                calendar_today
              </span>
              {lastEventDateStr}
            </span>
            <span className="flex items-center gap-1">
              <span className="material-icons text-gray-400 dark:text-gray-500" style={{ fontSize: '14px' }}>
                schedule
              </span>
              {lastEventTimeStr}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default IntegrationEventCard
